'use client';

import { useEffect, useRef, useState } from 'react';

const prefersReducedMotion = () => typeof window !== 'undefined' && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

/**
 * Adds `visibleClass` to the element once it enters the viewport (one-shot).
 * Pair with the `.reveal` styles in the landing CSS module.
 */
export const useReveal = <T extends HTMLElement = HTMLDivElement>(visibleClass: string, threshold = 0.15) => {
    const ref = useRef<T | null>(null);

    useEffect(() => {
        const el = ref.current;
        if (!el) return;
        if (prefersReducedMotion() || !('IntersectionObserver' in window)) {
            el.classList.add(visibleClass);
            return;
        }
        const observer = new IntersectionObserver(
            (entries) => {
                entries.forEach((entry) => {
                    if (entry.isIntersecting) {
                        entry.target.classList.add(visibleClass);
                        observer.unobserve(entry.target);
                    }
                });
            },
            { threshold, rootMargin: '0px 0px -8% 0px' }
        );
        observer.observe(el);
        return () => observer.disconnect();
    }, [visibleClass, threshold]);

    return ref;
};

/**
 * One-shot reveal for every child matching `selector` inside the container.
 * Stagger is handled in CSS via `--reveal-delay` custom properties.
 */
export const useRevealGroup = <T extends HTMLElement = HTMLDivElement>(visibleClass: string, selector = '[data-reveal]') => {
    const ref = useRef<T | null>(null);

    useEffect(() => {
        const container = ref.current;
        if (!container) return;
        const items = Array.from(container.querySelectorAll<HTMLElement>(selector));
        if (!items.length) return;
        if (prefersReducedMotion() || !('IntersectionObserver' in window)) {
            items.forEach((item) => item.classList.add(visibleClass));
            return;
        }
        const observer = new IntersectionObserver(
            (entries) => {
                entries.forEach((entry) => {
                    if (entry.isIntersecting) {
                        entry.target.classList.add(visibleClass);
                        observer.unobserve(entry.target);
                    }
                });
            },
            { threshold: 0.15, rootMargin: '0px 0px -8% 0px' }
        );
        items.forEach((item) => observer.observe(item));
        return () => observer.disconnect();
    }, [visibleClass, selector]);

    return ref;
};

/**
 * Eased count-up that starts when `started` becomes true.
 * Returns a Persian-formatted string.
 */
export const useCountUp = (target: number, started: boolean, duration = 1600, decimals = 0) => {
    const [display, setDisplay] = useState(() => (0).toLocaleString('fa-IR', { maximumFractionDigits: decimals }));

    useEffect(() => {
        if (!started) return;
        if (prefersReducedMotion()) {
            setDisplay(target.toLocaleString('fa-IR', { maximumFractionDigits: decimals }));
            return;
        }
        let raf = 0;
        const start = performance.now();
        const tick = (now: number) => {
            const progress = Math.min((now - start) / duration, 1);
            const eased = 1 - Math.pow(1 - progress, 4); // easeOutQuart
            setDisplay((target * eased).toLocaleString('fa-IR', { maximumFractionDigits: decimals }));
            if (progress < 1) raf = requestAnimationFrame(tick);
        };
        raf = requestAnimationFrame(tick);
        return () => cancelAnimationFrame(raf);
    }, [started, target, duration, decimals]);

    return display;
};

/**
 * Scroll-linked parallax for decorative layers (rAF-throttled, small deltas).
 * Moves the element by `scrollY * speed` on the Y axis.
 */
export const useParallax = <T extends HTMLElement = HTMLDivElement>(speed = 0.08) => {
    const ref = useRef<T | null>(null);

    useEffect(() => {
        const el = ref.current;
        if (!el || prefersReducedMotion()) return;
        let raf = 0;
        let ticking = false;

        const update = () => {
            ticking = false;
            el.style.transform = `translate3d(0, ${Math.round(window.scrollY * speed)}px, 0)`;
        };
        const onScroll = () => {
            if (!ticking) {
                ticking = true;
                raf = requestAnimationFrame(update);
            }
        };
        el.style.willChange = 'transform';
        window.addEventListener('scroll', onScroll, { passive: true });
        return () => {
            window.removeEventListener('scroll', onScroll);
            cancelAnimationFrame(raf);
            el.style.willChange = '';
        };
    }, [speed]);

    return ref;
};
